import { omit } from 'lodash';

import { AppCompanyFragment } from '@willow/graphql-iso/src/app';
import {
  assertNever,
  BorrowerRequestSettings,
  Company,
  CompanyAdminSettings,
  CompanyTaskSettings,
  DisburseEscrowFundsTaskSettings,
  TaskType,
} from '@willow/types-iso';

/**
 * Check tasks associated with an enabled setting
 */
export function getDisabledTaskTypesForCompany(company: Company): Set<TaskType>;
export function getDisabledTaskTypesForCompany(company: AppCompanyFragment): Set<TaskType>;
export function getDisabledTaskTypesForCompany(company: Company | AppCompanyFragment): Set<TaskType> {
  const disabledTaskTypes: Set<TaskType> = new Set();

  /* TASK SETTINGS */
  Object.entries(omit(company.taskSettings, '__typename')).forEach(([taskType, enabled]) => {
    const taskTypeEnum = taskType as keyof CompanyTaskSettings;
    switch (taskTypeEnum) {
      case 'pastGraceEnabled':
        if (!enabled) disabledTaskTypes.add('pastGrace');
        break;
      case 'loanFlaggedTooLongEnabled':
        if (!enabled) disabledTaskTypes.add('loanFlaggedTooLong');
        break;
      case 'forwardMiscollectedFundsEnabled':
        if (!enabled) disabledTaskTypes.add('forwardMiscollectedFunds');
        break;
      case 'mailPreActiveWelcomeLetterEnabled':
        if (!enabled) disabledTaskTypes.add('mailPreActiveWelcomeLetter');
        break;
      case 'mailMonthlyStatementEnabled':
        if (!enabled) disabledTaskTypes.add('mailMonthlyStatement');
        break;
      case 'mailGoodbyeLetterEnabled':
        if (!enabled) disabledTaskTypes.add('mailGoodbyeLetter');
        break;
      case 'mailMortgageeLetterEnabled':
        if (!enabled) disabledTaskTypes.add('mailMortgageeLetter');
        break;
      case 'mailInterestRateAdjustmentNotificationEnabled':
        if (!enabled) disabledTaskTypes.add('mailInterestRateAdjustmentNotification');
        break;
      case 'disburseEscrowFunds': {
        // Other task settings control this same task type
        if (
          !(enabled as DisburseEscrowFundsTaskSettings).enabled &&
          !company.taskSettings.disburseMortgageInsuranceEscrowFundsEnabled &&
          !company.taskSettings.disburseMortgageInsuranceNonEscrowedEnabled
        ) {
          disabledTaskTypes.add('disburseEscrowFunds');
          disabledTaskTypes.add('disburseEscrowFundsToBorrower');
        }
        break;
      }
      case 'disburseMortgageInsuranceEscrowFundsEnabled':
      case 'disburseMortgageInsuranceNonEscrowedEnabled': {
        // Only disable task types when ALL related settings are disabled
        if (
          !company.taskSettings.disburseEscrowFunds.enabled &&
          !company.taskSettings.disburseMortgageInsuranceEscrowFundsEnabled &&
          !company.taskSettings.disburseMortgageInsuranceNonEscrowedEnabled
        ) {
          disabledTaskTypes.add('disburseEscrowFunds');
          disabledTaskTypes.add('disburseEscrowFundsToBorrower');
        }
        break;
      }
      case 'generatePayoffStatementEnabled':
        if (!enabled) disabledTaskTypes.add('generatePayoffStatement');
        break;
      case 'achPaymentFailedEnabled':
        if (!enabled) disabledTaskTypes.add('achPaymentFailed');
        break;
      case 'generateManualStatementEnabled':
        if (!enabled) disabledTaskTypes.add('generateManualStatement');
        break;
      case 'welcomeCallTaskEnabled':
        if (!enabled) disabledTaskTypes.add('welcomeCall');
        break;
      case 'lienReleaseEnabled':
        if (!enabled) {
          disabledTaskTypes.add('partialLienRelease');
          disabledTaskTypes.add('fullLienRelease');
        }
        break;
      case 'reviewDeferralOfferPaymentEnabled':
        if (!enabled) disabledTaskTypes.add('reviewDeferralOfferPayment');
        break;
      case 'borrowerSubmittedDocumentEnabled':
        if (!enabled) disabledTaskTypes.add('borrowerSubmittedDocument');
        break;
      default:
        assertNever(taskTypeEnum);
    }
  });

  /* PORTAL SETTINGS */
  Object.entries(omit(company.portalSettings.borrowerRequests, '__typename')).forEach(([requestType, enabled]) => {
    const requestTypeEnum = requestType as keyof BorrowerRequestSettings;
    switch (requestTypeEnum) {
      case 'payoffRequestEnabled':
        if (!enabled) disabledTaskTypes.add('generateRequestedPayoff');
        break;
      case 'drawRequestEnabled':
        if (!enabled) disabledTaskTypes.add('drawRequest');
        break;
      case 'generalInquiryEnabled':
        if (!enabled) disabledTaskTypes.add('generalInquiry');
        break;
      case 'paymentAssistanceInquiryEnabled':
        if (!enabled) disabledTaskTypes.add('paymentAssistanceInquiry');
        break;
      case 'escrowWaiverRequestEnabled':
        if (!enabled) disabledTaskTypes.add('escrowWaiverRequest');
        break;
      case 'pmiCancellationRequestEnabled':
        if (!enabled) disabledTaskTypes.add('pmiCancellationRequest');
        break;
      case 'verificationOfMortgageEnabled':
        if (!enabled) disabledTaskTypes.add('verificationOfMortgageRequest');
        break;
      default:
        assertNever(requestTypeEnum);
    }
  });

  /* ADMIN SETTINGS */
  Object.entries(omit(company.adminSettings, '__typename')).forEach(([adminSetting, enabled]) => {
    const adminSettingEnum = adminSetting as keyof CompanyAdminSettings;
    switch (adminSettingEnum) {
      case 'enableUsioPayments':
        if (!enabled) disabledTaskTypes.add('disableUsioAccount');
        break;
      case 'repayEnabled':
        if (!enabled) disabledTaskTypes.add('disableRepayAccount');
        break;
      case 'mortgageeLettersEnabled':
        if (!enabled) disabledTaskTypes.add('mailMortgageeLetter');
        break;
      case 'manualStatementGeneration':
        if (!enabled) disabledTaskTypes.add('generateManualStatement');
        break;
      case 'loanWorkflowsEnabled':
        if (!enabled) {
          // Disable workflow-related tasks
          disabledTaskTypes.add('foreclosureStepDemandLetters');
          disabledTaskTypes.add('foreclosureStepDemandLetterExpires');
          disabledTaskTypes.add('foreclosureStepReferClosureAttorney');
          disabledTaskTypes.add('foreclosureStepJudicialState');
          disabledTaskTypes.add('foreclosureStepNonJudicialState');
          disabledTaskTypes.add('foreclosureStepHomeSale');
          disabledTaskTypes.add('reoStepEvictionOfOccupiedProperty');
          disabledTaskTypes.add('reoStepOrderInspectionAndValuation');
          disabledTaskTypes.add('reoStepRepairsAndPreservation');
          disabledTaskTypes.add('reoStepPropertyListing');
          disabledTaskTypes.add('reoStepPropertyReduction');
          disabledTaskTypes.add('reoStepOfferEvaluations');
          disabledTaskTypes.add('reoStepCounterOfferDetails');
          disabledTaskTypes.add('reoStepContractStatus');
          disabledTaskTypes.add('chapter7StepFilingStay');
          disabledTaskTypes.add('chapter7StepCreditorMeeting');
          disabledTaskTypes.add('chapter7StepAttorneyRelief');
          disabledTaskTypes.add('chapter7StepHearingDate');
          disabledTaskTypes.add('chapter7StepHearingOutcome');
        }
        break;
      case 'chapter13WorkflowEnabled':
        if (!enabled) {
          disabledTaskTypes.add('chapter13StepFilingStay');
          disabledTaskTypes.add('chapter13StepAttorneyReferral');
          disabledTaskTypes.add('chapter13StepCreditorMeeting');
          disabledTaskTypes.add('chapter13StepPlanInPlace');
          disabledTaskTypes.add('chapter13StepPlanMonitoring');
        }
        break;

      case 'generate1098Enabled':
        if (!enabled) disabledTaskTypes.add('mail1098Form');
        break;
      case 'whedaEnabled':
      case 'loanExtensionAgreementDocumentEnabled':
      case 'loanExtensionFeesDocumentEnabled':
      case 'sendTransferEmailImmediately':
      case 'autopayEnabled':
      case 'investorRemittanceEnabled':
      case 'disableBorrowerNotifications':
      case 'multiplePropertiesEnabled':
      case 'generate1098InterestThreshold':
      case 'automateNonPerformingLoans':
      case 'enableEncryptedAccountDetailsStorage':
      case 'insuranceTaxTrackingEnabled':
      case 'newInsuranceFieldsEnabled':
      case 'skipEscrowTrackingWithin60Days':
      case 'applicationsEnabled':
      case 'applicationsEnabledSettings':
      case 'econsentEnabled':
      case 'printAndMailEnabled':
      case 'billingSettings':
      case 'investorView':
      case 'mfaAppUsersEnabled':
      case 'creditPendingPaymentsOnPayoff':
      case 'exactPaymentsOnly':
      case 'noteflowEnabled':
      case 'wipInvestorReporting':
      case 'glCodesReportEnabled':
      case 'workflowsV2Enabled':
      case 'allowlistEmailDomains':
      case 'teamsEnabled':
      case 'poolManagement':
      case 'investorDocumentsEnabled':
      case 'investorAuditLogEnabled':
      case 'includePayoffFundingDateEnabled':
      case 'eliteFees':
      case 'enableBorrowerInsuranceExpirationEmailReminders':
      case 'wipCreditReporting':
      case 'aiLoanNotesSummaryEnabled':
      case 'aiEmailsEnabled':
      case 'enableSameDayACH':
      case 'dwollaSingleTransactionEnabled':
      case 'repayProperAccountRelationship':
      case 'repayDirectBankIntegration':
      case 'customTemplates':
      case 'shouldValidateBreakdownsNotMutated':
      case 'interestOnEscrow':
      case 'escrowAdvanceReporting':
      case 'manualNachaFileGeneration':
      case 'dataExportEnabled':
      case 'wipFileImport':
      case 'wipLiquidation':
      case 'wipBankruptcyCh13WorkoutPlan':
      case 'payeeManagement':
      case 'ledgerEnabled':
      case 'operatingTimezone':
        break;
      default:
        assertNever(adminSettingEnum);
    }
  });

  return disabledTaskTypes;
}
